import React, { useEffect, useRef, useState } from 'react';
import type { PopupActions } from 'reactjs-popup/dist/types';
import { useTheme } from 'styled-components/macro';

import FlexContainer from 'components/FlexContainer/FlexContainer';
import { getPopupContentStyle } from 'components/UserInfo/UserInfo.styled';
import type { LatestEulaResponseDTO } from 'declarations/dto/eula';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { getCurrentUserId } from 'helpers/user';

import {
  createNewEulaAgreement,
  getLatestEula,
  getUsersLatestEulaAgreementStatus,
} from '../../api/eula';
import useUserInfo from '../UserInfo/hooks/useUserInfo';

import {
  EulaButtonStyled,
  EulaPopupStyled,
  TypographyContentStyled,
  TypographyTitleStyled,
} from './EulaAgreement.styled';

type EulaAgreementProps = Record<string, never>;

const EulaAgreement: React.FC<EulaAgreementProps> = () => {
  const [showEulaAgreement, setShowEulaAgreement] = useState(false);
  const [eulaContent, setEulaContent] = useState<LatestEulaResponseDTO>({
    id: '',
    version: '',
    releaseDate: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const menuPopupRef = useRef<PopupActions>(null);
  const theme = useTheme();
  const { handleSignOutClick } = useUserInfo();
  const userId = getCurrentUserId();

  useEffect(() => {
    const fetchEulaAccepted = async (): Promise<void> => {
      try {
        setLoading(true);
        let statusResponse = false;
        if (userId) {
          statusResponse = await getUsersLatestEulaAgreementStatus(userId);
          if (!statusResponse) {
            setEulaContent(await getLatestEula());
          }
        } else {
          return;
        }
        setShowEulaAgreement(!statusResponse);
      } catch (error) {
        // Error fetching EULA - user will see EULA popup if needed, no additional notification required
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchEulaAccepted();
  }, [userId]);

  const handleAgree = async (): Promise<void> => {
    if (userId) {
      const request = {
        eulaId: eulaContent.id,
        userId: userId,
        accepted: true,
      };
      await createNewEulaAgreement(request);
      setShowEulaAgreement(false);
      window.location.reload();
    } else {
      throw new Error('Missing Id token.');
    }
  };

  const handleDisagree = async (): Promise<void> => {
    if (userId) {
      const request = {
        eulaId: eulaContent.id,
        userId: userId,
        accepted: false,
      };
      await createNewEulaAgreement(request);
      setShowEulaAgreement(false);
      handleSignOutClick();
    } else {
      throw new Error('Missing Id token.');
    }
  };

  const getDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <>
      {showEulaAgreement && (
        <FlexContainer direction="column" align="center">
          <EulaPopupStyled
            offsetY={4}
            arrow={false}
            ref={menuPopupRef}
            position={'center center'}
            contentStyle={getPopupContentStyle(theme, false)}
            popupId={ScrollLockIdE.EulaPopup}
            isOpen={showEulaAgreement}
            modal={true}
            closeOnEscape={false}
            closeOnDocumentClick={false}
          >
            <FlexContainer
              direction="column"
              align="center"
              style={{ maxHeight: '600px', minWidth: '600px' }}
            >
              <TypographyTitleStyled variant="h2" color="dark" align="center">
                End User License Agreement
                <br />
                {loading ? 'Loading...' : 'Released: ' + getDate(eulaContent?.releaseDate)}
              </TypographyTitleStyled>
              <TypographyContentStyled
                variant="body1"
                color="black"
                align="center"
                style={{ maxHeight: '400px', overflow: 'auto' }}
              >
                {eulaContent?.description}
              </TypographyContentStyled>
              <FlexContainer justify={false ? 'center' : 'flex-end'}>
                <EulaButtonStyled onClick={handleDisagree} variant="outline">
                  Decline
                </EulaButtonStyled>
                <EulaButtonStyled onClick={handleAgree} disabled={loading}>
                  {loading ? 'Loading...' : 'Accept'}
                </EulaButtonStyled>
              </FlexContainer>
            </FlexContainer>
          </EulaPopupStyled>
        </FlexContainer>
      )}
    </>
  );
};

export default EulaAgreement;
